/** Optional image selection. Destinations/data and committing a selection remain host-owned. */
class VoltImageOptions extends HTMLElement {
  #events;
  #observer;
  #drag;
  #suppressClick = false;
  #hoverTimer;
  #pendingUrl;
  connectedCallback() {
    this.#events?.abort();
    this.#events = new AbortController();
    queueMicrotask(() => {
      if (!this.isConnected || this.#events.signal.aborted) return;
      const signal = this.#events.signal;
      const rail = this.querySelector('.volt-image-options__rail');
      if (!rail) return;
      for (const link of rail.querySelectorAll('a[data-image-option]')) {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = link.className;
        button.dataset.url = link.getAttribute('href');
        button.dataset.label = link.dataset.label;
        button.setAttribute('aria-label', link.getAttribute('aria-label'));
        button.setAttribute('aria-pressed', String(link.hasAttribute('aria-current')));
        button.append(...link.childNodes);
        link.replaceWith(button);
      }
      const scrubber = this.querySelector('.volt-image-options__scrubber');
      const update = () => {
        const total = rail.scrollWidth;
        const visible = rail.clientWidth;
        const max = Math.max(0, total - visible);
        const offset = Math.min(max, Math.max(0, rail.scrollLeft));
        const progress = this.querySelector('.volt-image-options__progress');
        if (progress) {
          progress.hidden = max <= 1;
          progress.style.setProperty('--image-options-fill', String(total ? (visible + offset) / total : 1));
          if (scrubber) scrubber.value = String(max ? Math.round(offset / max * 100) : 0);
          progress.setAttribute('aria-valuenow', String(Math.round(total ? (visible + offset) / total * 100 : 100)));
        }
      };
      if (scrubber) {
        scrubber.disabled = false;
        scrubber.addEventListener('input', () => {
          rail.scrollLeft = Number(scrubber.value) / 100 * Math.max(0, rail.scrollWidth - rail.clientWidth);
          update();
        }, { signal });
      }
      this.addEventListener('pointerdown', event => event.stopPropagation(), { signal });
      rail.addEventListener('scroll', () => { this.cancelHover(); update(); }, { passive: true, signal });
      this.#observer = new ResizeObserver(update);
      this.#observer.observe(rail);
      this.addEventListener('keydown', event => { this.cancelHover(); if (event.key !== 'Escape') event.stopPropagation(); }, { signal });
      this.addEventListener('dragstart', event => event.preventDefault(), { signal });
      rail.addEventListener('pointerover', event => {
        if (!this.hasAttribute('data-hover-select') || event.pointerType !== 'mouse' || event.buttons || this.#drag || this.#suppressClick || !window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
        const option = event.target.closest('button[data-url]');
        if (!option || option.contains(event.relatedTarget)) return;
        this.cancelHover();
        this.#hoverTimer = setTimeout(() => {
          this.#hoverTimer = undefined;
          if (this.isConnected && !this.#drag && (option.getAttribute('aria-pressed') !== 'true' || this.#pendingUrl) && !option.hasAttribute('data-pending')) this.#activate(option, 'hover');
        }, 120);
      }, { signal });
      rail.addEventListener('pointerout', event => {
        const option = event.target.closest('button[data-url]');
        if (!option?.contains(event.relatedTarget)) this.cancelHover();
      }, { signal });
      rail.addEventListener('pointerdown', event => {
        this.cancelHover();
        event.stopPropagation();
        this.#suppressClick = false;
        if (event.pointerType === 'mouse' && event.button === 0) {
          this.#drag = { id: event.pointerId, x: event.clientX, offset: rail.scrollLeft, moved: false };
        }
      }, { signal });
      rail.addEventListener('pointermove', event => {
        const drag = this.#drag;
        if (!drag || drag.id !== event.pointerId) return;
        const delta = event.clientX - drag.x;
        if (!drag.moved && Math.abs(delta) < 6) return;
        drag.moved = true;
        rail.setPointerCapture(event.pointerId);
        rail.scrollLeft = drag.offset - delta;
        event.preventDefault();
        event.stopPropagation();
      }, { signal });
      const endDrag = event => {
        event.stopPropagation();
        if (this.#drag?.id === event.pointerId) {
          this.#suppressClick = event.type === 'pointerup' && this.#drag.moved;
          this.#drag = undefined;
          if (rail.hasPointerCapture(event.pointerId)) rail.releasePointerCapture(event.pointerId);
        }
      };
      rail.addEventListener('pointerup', endDrag, { signal });
      rail.addEventListener('pointercancel', endDrag, { signal });
      this.addEventListener('click', event => {
        this.cancelHover();
        const option = event.target.closest('button[data-url]');
        event.stopPropagation();
        if (this.#suppressClick && event.detail !== 0) { this.#suppressClick = false; event.preventDefault(); return; }
        if (!option) return;
        this.#activate(option, event.detail === 0 ? 'keyboard' : 'pointer');
      }, { signal });
      update();
    });
  }
  #activate(option, activation) {
    const selection = new CustomEvent('volt:image-option-select', {
      bubbles: true, cancelable: true,
      detail: { url: option.dataset.url, label: option.dataset.label, activation },
    });
    if (this.dispatchEvent(selection)) window.location.assign(option.dataset.url);
  }
  cancelHover() { clearTimeout(this.#hoverTimer); this.#hoverTimer = undefined; }
  /** Quiet visual feedback; pressed state remains committed by the host. */
  pending(url, pointer = false) {
    this.#pendingUrl = url;
    this.toggleAttribute('data-pointer-selection', pointer);
    for (const option of this.querySelectorAll('button[data-url]')) option.toggleAttribute('data-pending', option.dataset.url === url);
  }
  /** Commit only after the host has applied the corresponding content successfully. */
  select(url) {
    for (const option of this.querySelectorAll('button[data-url]')) {
      option.setAttribute('aria-pressed', String(option.dataset.url === url));
    }
    const option = [...this.querySelectorAll('button[data-url]')].find(item => item.dataset.url === url);
    const label = this.querySelector('.volt-image-options__label');
    if (option && label && !label.hasAttribute('data-static-heading')) label.textContent = option.dataset.label;
  }
  disconnectedCallback() {
    this.cancelHover();
    this.#events?.abort();
    this.#observer?.disconnect();
    this.#drag = undefined;
  }
}
if (!customElements.get('volt-image-options')) customElements.define('volt-image-options', VoltImageOptions);
